import React, { useState, useEffect, useMemo } from 'react';
import { X, Download, Smartphone, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import api from '../utils/api';

const InstallPWA = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  // Check if iOS device
  const isIOS = useMemo(() => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }, []);

  // Handle PWA updates
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          console.log('[PWA] New version available:', event.data.version);
          setShowUpdateBanner(true);
        }
      });

      // Check for waiting service worker on load
      navigator.serviceWorker.ready.then(registration => {
        // Check if there's a waiting worker
        if (registration.waiting) {
          setWaitingWorker(registration.waiting);
          setShowUpdateBanner(true);
        }

        // Listen for new service worker
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New content is available
                setWaitingWorker(newWorker);
                setShowUpdateBanner(true);
              }
            });
          }
        });
      });

      // Check for updates every 5 minutes
      const checkForUpdates = () => {
        navigator.serviceWorker.ready.then(registration => {
          registration.update().catch(err => {
            console.log('[PWA] Update check failed:', err);
          });
        });
      };

      const updateInterval = setInterval(checkForUpdates, 5 * 60 * 1000);
      
      // Also check on visibility change (when user comes back to tab)
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          checkForUpdates();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        clearInterval(updateInterval);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, []);

  // Handle update button click
  const handleUpdate = () => {
    if (waitingWorker) {
      // Tell waiting worker to skip waiting
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
    // Reload the page to get new content
    window.location.reload();
  };


  // Detect device info for tracking
  const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    let deviceType = 'desktop';
    let platform = 'unknown';
    let browser = 'unknown';

    // Detect device type
    if (/Mobile|Android|iPhone|iPad|iPod/.test(ua)) {
      deviceType = /iPad|Tablet/.test(ua) ? 'tablet' : 'mobile';
    }

    // Detect platform
    if (/Android/.test(ua)) platform = 'android';
    else if (/iPhone|iPad|iPod/.test(ua)) platform = 'ios';
    else if (/Windows/.test(ua)) platform = 'windows';
    else if (/Mac/.test(ua)) platform = 'mac';
    else if (/Linux/.test(ua)) platform = 'linux';

    // Detect browser
    if (/Chrome/.test(ua) && !/Edg/.test(ua)) browser = 'chrome';
    else if (/Safari/.test(ua) && !/Chrome/.test(ua)) browser = 'safari';
    else if (/Firefox/.test(ua)) browser = 'firefox';
    else if (/Edg/.test(ua)) browser = 'edge';

    return {
      device_type: deviceType,
      platform: platform,
      browser: browser,
      screen_width: window.screen.width,
      screen_height: window.screen.height
    };
  };

  // Track PWA installation
  const trackInstall = async () => {
    try {
      const deviceInfo = getDeviceInfo();
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      await api.post('/pwa/track-install', {
        ...deviceInfo,
        user_id: user.id || null,
        merchant_id: user.merchant_id || user.id || null
      });
      console.log('PWA install tracked successfully');
    } catch (error) {
      console.error('Error tracking PWA install:', error);
    }
  };

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return; // Already installed as PWA
    }

    // Check if we should show the banner (not shown in last 7 days)
    const lastDismissed = localStorage.getItem('pwa-banner-dismissed');
    if (lastDismissed) {
      const dismissedDate = new Date(lastDismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    // Listen for install prompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowBanner(true);
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      console.log('PWA was installed');
      trackInstall();
      setShowBanner(false);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // For iOS, show banner after a short delay
    if (isIOS) {
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.removeEventListener('appinstalled', handleAppInstalled);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isIOS]);

  const handleInstall = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') {
        // Track will be handled by appinstalled event
        setShowBanner(false);
      }
      setInstallPrompt(null);
    } else if (isIOS) {
      setShowIOSInstructions(true);
      // Track iOS instruction views as potential installs
      trackInstall();
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-banner-dismissed', new Date().toISOString());
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Install Banner */}
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-2xl p-4 z-50 animate-in slide-in-from-bottom-4">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="bg-white/20 p-2 rounded-lg">
            <Smartphone className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-sm">Install ShipyBox App</h3>
            <p className="text-xs text-blue-100 mt-1">
              Get quick access from your home screen. Works offline too!
            </p>
          </div>
        </div>
        
        <Button 
          onClick={handleInstall}
          className="w-full mt-3 bg-white text-blue-600 hover:bg-blue-50 font-medium"
          size="sm"
        >
          <Download className="w-4 h-4 mr-2" />
          Install Now
        </Button>
      </div>

      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 relative animate-in zoom-in-95">
            <button 
              onClick={() => setShowIOSInstructions(false)}
              className="absolute top-4 right-4 p-1 hover:bg-gray-100 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Install on iPhone/iPad</h3>
              <p className="text-sm text-gray-500 mt-2">Follow these steps:</p>
            </div>
            
            <ol className="mt-4 space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium">1</span>
                <span>Tap the <strong>Share</strong> button <span className="inline-block w-5 h-5 bg-gray-200 rounded text-center text-xs leading-5">↑</span> at the bottom of Safari</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium">2</span>
                <span>Scroll down and tap <strong>&quot;Add to Home Screen&quot;</strong></span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-blue-600 text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium">3</span>
                <span>Tap <strong>&quot;Add&quot;</strong> in the top right corner</span>
              </li>
            </ol>
            
            <Button 
              onClick={() => setShowIOSInstructions(false)}
              className="w-full mt-6"
            >
              Got it!
            </Button>
          </div>
        </div>
      )}

      {/* Update Available Banner */}
      {showUpdateBanner && (
        <div className="fixed top-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl shadow-2xl p-4 z-50 animate-in slide-in-from-top-4">
          <button 
            onClick={() => setShowUpdateBanner(false)}
            className="absolute top-2 right-2 p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          
          <div className="flex items-start gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <RefreshCw className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm">Update Available!</h3>
              <p className="text-xs text-green-100 mt-1">
                A new version of ShipyBox is available with new features and improvements.
              </p>
              <Button 
                onClick={handleUpdate}
                size="sm"
                className="mt-3 bg-white text-green-700 hover:bg-green-50 w-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Update Now
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWA;
